import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Countries } from '@xoda/common'
import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Api } from '../../api'
import { formatMobile } from '../../helpers/others'
import { useCountdown } from '../../hooks/countdown'
import { useGet } from '../../hooks/get'
import { useSwitch } from '../../hooks/switch'
import { useTc } from '../../hooks/tc'
import { useUser } from '../../hooks/user'
import { GoodsService, GoodsType } from '../../services/goods'
import { configState } from '../../store/config'
import { gymState } from '../../store/gym'
import { themeMeta } from '../../theme/meta'
import { Checkout } from '../Checkout'
import { Fields, Form, FormProps } from '../Form'
import { GoodsNew } from '../GoodsNew'
import { Modal, ModalRef } from '../Modal'
import { OtpInput } from '../OtpInput'
import { PaymentMethodForm } from '../PaymentMethodForm'
import { Promo, PromoCodeField } from '../PromoCodeField'
import { SessionSummary } from '../SessionSummary'
import { BankAccountFieldsProps } from './BankAccountFields'
import { CreditCardFieldsProps } from './CreditCardFields'
import { Result } from './Result'
import { SignModalsContext } from './SignModals'

const SignUpModal: FC<SignUpModalProps> = (props) => {
  const context = useContext(SignModalsContext)

  const { onFinish, onCancel } = props

  const config = useRecoilValue(configState)
  const gym = useRecoilValue(gymState)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: apiPacks } = useGet<Dto.Pack[]>([
    'member_portal/packs',
    { ...(context.categoryIds && { category_id: context.categoryIds }) },
  ])
  const { data: apiPlans } = useGet<Dto.Plan[]>([
    'member_portal/plans',
    { ...(context.categoryIds && { category_id: context.categoryIds }) },
  ])

  const { signIn } = useUser()
  const {
    value: codeCountdownValue,
    start: starCodeCountdown,
    running: codeCountdownRunning,
  } = useCountdown()
  const { on: opened, close } = useSwitch(true)
  const { on: pending, open: startPending, close: stopPending } = useSwitch()

  const [step, setStep] = useState(0)
  const [lead, setLead] = useState<Dto.Lead>()
  const [selectedGoods, setSelectedGoods] = useState<GoodsType>()
  const [promo, setPromo] = useState<Promo>()

  const baseParams = useMemo(() => {
    return {
      lead_id: String(lead?.id),
      ...(context.purchase
        ? {
            type: GoodsService.getType(context.purchase.item)!,
            id: String(context.purchase.item.id),
            [`${GoodsService.getType(context.purchase.item)}_id`]:
              context.purchase.item.id,
            ...(context.purchase.promo && {
              promo_code: context.purchase.promo.code,
            }),
          }
        : selectedGoods
        ? {
            type: GoodsService.getType(selectedGoods),
            id: String(selectedGoods.id),
            [`${GoodsService.getType(selectedGoods)}_id`]: String(
              selectedGoods.id
            ),
            ...(promo && {
              promo_code: promo.code,
            }),
          }
        : undefined),
    }
  }, [context.purchase, lead?.id, promo, selectedGoods])

  const { data: checkout } = useGet<Dto.Checkout>(() => {
    if (!lead || gym?.payment_method === 'payrix') {
      return
    }

    return [
      'member_portal/checkout',
      {
        lead_id: lead.id,
        success_url: String(
          new URL(
            `/stripe?${new URLSearchParams(baseParams)}`,
            window.location.origin
          )
        ),
        cancel_url: String(new URL('/classes', window.location.origin)),
      },
    ]
  })

  const { tcElement, paymentTcElement, formTc, gymTc } = useTc(
    selectedGoods,
    checkout
  )

  const personalFields = useRef<Fields>()
  const codeType = useRef<string>()
  const codeId = useRef<number>()
  const modal = useRef<ModalRef>(null)

  const redirectingPayment =
    (checkout && ['stripe', 'paychoice'].includes(checkout.payment.method)) ||
    gym?.payment_method === 'payrix'
  const cartPackage = context.purchase?.item || selectedGoods
  const packageFree = cartPackage
    ? !GoodsService.getRoughPrice(cartPackage)
    : false

  const handleRequestClose = useCallback(() => {
    close()
    onCancel?.()

    if (step >= 4) {
      onFinish?.()
    }
  }, [close, onCancel, onFinish, step])

  const sendCode = useCallback(async () => {
    if (!personalFields.current) {
      return
    }

    codeId.current = (
      await Api.post('verification/codes', {
        type: codeType.current,
        receiver:
          codeType.current === 'sms'
            ? formatMobile(
                personalFields.current.countryCode,
                personalFields.current.mobile
              )
            : codeType.current === 'email'
            ? personalFields.current.email
            : undefined,
      })
    ).id
    starCodeCountdown()
  }, [starCodeCountdown])

  const go = useCallback((offset = 1) => {
    setStep((prev) => prev + offset)
    modal.current?.toTop()
  }, [])

  const handleBackClick = useCallback(() => {
    if (step === 3) {
      go(-2)
      return
    }

    go(-1)
  }, [go, step])

  const handlePersonSubmit: FormProps['onSubmit'] = useCallback(
    async (fields) => {
      try {
        startPending()
        setLead(
          await Api.post('member_portal/leads', {
            first_name: fields.firstName,
            last_name: fields.lastName,
            email: fields.email,
            mobile: formatMobile(fields.countryCode, fields.mobile),
            source: fields.source,
          })
        )
        personalFields.current = fields
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [go, startPending, stopPending]
  )

  const handleCodeTypeSubmit: FormProps['onSubmit'] = useCallback(
    async (fields) => {
      try {
        startPending()
        codeType.current = fields.codeType
        await sendCode()
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [go, sendCode, startPending, stopPending]
  )

  const handleVerifySubmit: FormProps['onSubmit'] = useCallback(
    async (fields) => {
      try {
        startPending()
        await Api.post('verification/check', {
          id: codeId.current,
          code: fields.code,
        })
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [go, startPending, stopPending]
  )

  const handlePaySucceed: (
    | CreditCardFieldsProps
    | BankAccountFieldsProps
  )['onSucceed'] = useCallback(
    async (payment) => {
      try {
        startPending()
        const body = await Api.post('member_portal/members', {
          payment,
          ...(formTc
            ? { terms: { form_id: formTc.id, version: formTc.version } }
            : gymTc
            ? { terms: { id: gymTc.id } }
            : undefined),
          ...baseParams,
        })
        await signIn({
          memberId: body.member_id,
          leadId: body.lead_id,
          userId: body.user_id,
          email: personalFields.current!.email,
          token: body.token,
          firstName: body.first_name,
          lastName: body.last_name,
          gymId: Number(body.gym_id),
        })
        go()
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      } finally {
        stopPending()
      }
    },
    [baseParams, formTc, go, gymTc, signIn, startPending, stopPending]
  )

  const handlePackageNext = useCallback(() => {
    if (packageFree) {
      handlePaySucceed({ free: true })
      return
    }

    if (gym?.payment_method === 'payrix') {
      ;(async () => {
        try {
          const data = await Api.post('member_portal/checkout', {
            ...baseParams,
            redirect_url: new URL('/payrix', window.location.origin),
          })
          window.location.assign(data.redirect_url)
        } catch (error) {
          if (error instanceof Error) {
            toast.error(error.message)
          }
        }
        return
      })()
    }

    if (!checkout || !redirectingPayment) {
      return
    }

    if (checkout.payment.redirect_url) {
      window.location.assign(checkout.payment.redirect_url)
      return
    }

    if (checkout.payment.method === 'stripe') {
      const params = new URLSearchParams({
        ...baseParams,
        connected_account_id: checkout.payment.stripe_connected_account_id!,
        client_secret: checkout.payment.client_secret!,
        customer_id: checkout.payment.stripe_customer_id!,
      })
      navigate(`/stripe?${params}`)
      return
    }

    if (checkout.payment.method === 'paychoice') {
      const params = new URLSearchParams({
        ...baseParams,
        private_key: checkout.payment.paychoice_api_key!,
        username: checkout.payment.paychoice_account_guid!,
        customer_id: checkout.payment.paychoice_customer_id!,
        customer_reference: checkout.payment.customer_reference!,
      })
      navigate(`/paychoice?${params}`, {})
      return
    }
  }, [
    baseParams,
    checkout,
    gym?.payment_method,
    handlePaySucceed,
    navigate,
    packageFree,
    redirectingPayment,
  ])

  return (
    <Modal
      open={opened}
      fullWidth
      {...(step < 4 && {
        closesWithConfirm: true,
        actions: [
          ...(step > 0
            ? ([{ onClick: handleBackClick, children: t('Back') }] as const)
            : ([] as const)),
          {
            type: 'submit',
            form: `sign-up-${step}`,
            loading: pending,
            children: step === 1 ? t('Send code') : t('Next'),
            ...(step === 3 && { onClick: handlePackageNext }),
          },
        ],
      })}
      onClose={handleRequestClose}
      ref={modal}
    >
      {step === 0 && (
        <>
          <Box mb={3}>
            <Typography variant="h1" mb={1}>
              {t('Sign up now')}
            </Typography>
            <Typography>
              {t('Sign up to get started with your booking.')}
            </Typography>
          </Box>
          <Form id="sign-up-0" onSubmit={handlePersonSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('First name')}
                  name="firstName"
                  required
                  autoFocus
                  defaultValue={personalFields.current?.firstName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('Last name')}
                  name="lastName"
                  required
                  defaultValue={personalFields.current?.lastName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('Email')}
                  type="email"
                  name="email"
                  required
                  defaultValue={personalFields.current?.email}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('Mobile')}
                  type="tel"
                  name="mobile"
                  placeholder={t('Mobile number')}
                  hiddenLabel
                  defaultValue={personalFields.current?.mobile}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Select
                            name="countryCode"
                            required
                            defaultValue={personalFields.current?.countryCode}
                            sx={{ minInlineSize: '8em' }}
                          >
                            {Countries.sort('area-code').map((country) => (
                              <option
                                key={country.areaCode}
                                value={country.areaCode}
                              >
                                {`${country.flag} +${country.areaCode}`}
                              </option>
                            ))}
                          </Select>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              {config?.sourceOptions && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={t('How do you know about us?')}
                    name="source"
                    select
                    required
                    defaultValue={personalFields.current?.source}
                  >
                    {config.sourceOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
          </Form>
        </>
      )}
      {step === 1 && (
        <>
          <Box mb={3}>
            <Typography variant="h1" mb={1}>
              {t(
                'We need to send you a verification code to activate your account '
              )}
            </Typography>
          </Box>
          <Form id="sign-up-1" onSubmit={handleCodeTypeSubmit}>
            {personalFields.current && (
              <RadioGroup name="codeType" defaultValue="email">
                <FormControlLabel
                  control={<Radio />}
                  label={
                    <>
                      <Box component="span" fontWeight="bold">
                        {t('Email')}
                      </Box>{' '}
                      {personalFields.current.email}
                    </>
                  }
                  value="email"
                />
                {personalFields.current.mobile && (
                  <FormControlLabel
                    control={<Radio />}
                    label={
                      <>
                        <Box component="span" fontWeight="bold">
                          {t('Mobile')}
                        </Box>{' '}
                        {formatMobile(
                          personalFields.current.countryCode,
                          personalFields.current.mobile
                        )}
                      </>
                    }
                    value="sms"
                  />
                )}
              </RadioGroup>
            )}
          </Form>
        </>
      )}
      {step === 2 && (
        <>
          <Box mb={3}>
            <Typography variant="h1" mb={1}>
              {t('Verify your details')}
            </Typography>
            <Typography>
              {t(
                'Please enter the verification code we just sent to your email to continue.'
              )}
            </Typography>
          </Box>
          <Form id="sign-up-2" onSubmit={handleVerifySubmit}>
            <Box px={{ sm: 5 }}>
              <OtpInput name="code" />
              <Box mt={3} textAlign="end">
                {codeCountdownRunning ? (
                  <Typography color="textDisabled">
                    {codeCountdownValue}
                  </Typography>
                ) : (
                  <Typography>
                    <Link color="textSecondary" onClick={sendCode}>
                      {t('Resend code')}
                    </Link>
                  </Typography>
                )}
              </Box>
            </Box>
          </Form>
        </>
      )}
      {step === 3 && (
        <Grid
          container
          direction={{ xs: 'column-reverse', sm: 'row' }}
          alignItems="start"
          spacing={{ xs: 3, sm: 5 }}
        >
          <Grid size={{ xs: 12, sm: 7 }}>
            {!context.purchase && (
              <>
                <Box mb={5}>
                  <Box mb={3}>
                    <Typography variant="h1" mb={1}>
                      {t('Select a pack')}
                    </Typography>
                  </Box>
                  <GoodsNew
                    plans={apiPlans}
                    packs={apiPacks}
                    onSelect={setSelectedGoods}
                  />
                </Box>
                {selectedGoods && (
                  <Box mb={5}>
                    <Box mb={3}>
                      <Typography variant="h1" mb={1}>
                        {t('Promo code')}
                      </Typography>
                    </Box>
                    <PromoCodeField goods={selectedGoods} onApply={setPromo} />
                  </Box>
                )}
              </>
            )}
            {personalFields.current &&
              checkout &&
              !redirectingPayment &&
              !packageFree && (
                <Box mb={5}>
                  <Box mb={3}>
                    <Typography variant="h1" mb={1}>
                      {t('Add payment details')}
                    </Typography>
                    <Typography>
                      {t(
                        'Complete your payment using a credit card or bank details.'
                      )}
                    </Typography>
                  </Box>
                  <>
                    <PaymentMethodForm
                      id="sign-up-3"
                      lastName={personalFields.current.lastName}
                      payment={checkout.payment}
                      pending={pending}
                      tcElement={
                        <>
                          {tcElement}
                          {paymentTcElement}
                        </>
                      }
                      onSubmit={startPending}
                      onSucceed={handlePaySucceed}
                      onError={stopPending}
                    />
                  </>
                </Box>
              )}
          </Grid>
          <Grid
            size={{ xs: 12, sm: 5 }}
            container
            direction="column"
            spacing={2}
            position={{ sm: 'sticky' }}
            top={{ sm: 0 }}
          >
            {selectedGoods ? (
              <Grid>
                <Card sx={{ bgcolor: themeMeta.colors.monochrome.bg }}>
                  <CardContent>
                    <Checkout
                      goods={selectedGoods}
                      discountedPrice={promo?.discountedPrice}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ) : context.purchase ? (
              <Grid>
                <Card sx={{ bgcolor: themeMeta.colors.monochrome.bg }}>
                  <CardContent>
                    <Checkout
                      goods={context.purchase.item}
                      discountedPrice={context.purchase.promo?.discountedPrice}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ) : undefined}
            {context.session && (
              <Grid>
                <SessionSummary session={context.session} wrapped />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {step === 4 && <Result session={context.session} />}
    </Modal>
  )
}

type SignUpModalProps = {
  onFinish?: VoidFunction
  onCancel?: VoidFunction
}

export { SignUpModal }
export type { SignUpModalProps }
